import { Instagram, Linkedin, Youtube, MapPin, Phone, Mail } from "lucide-react";
import { trackLead } from "@/lib/analytics";

export default function Footer() {
  return (
    <footer className="bg-[#282829] text-white pt-16 pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="grid lg:grid-cols-4 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_shah-edu-3d/artifacts/k9mfo0sl_image.png"
                alt="Shah Overseas Education Logo — Overseas Education Consultancy"
                className="h-14 w-auto object-contain bg-white rounded-xl p-2 border-2 border-white"
                loading="lazy"
                decoding="async"
              />
              <div>
                <div className="font-display text-2xl">SHAH OVERSEAS</div>
                <div className="text-[10px] tracking-widest text-white/50">EDUCATION CONSULTANCY</div>
              </div>
            </div>
            <p className="mt-5 text-white/70 max-w-md leading-relaxed">
              Shah Overseas Education is one of the leading <b>study abroad consultants in Nagpur, India</b> — counselling students for <b>MS, MBA, Bachelor's & PhD programs</b> in the USA, UK, Canada, Australia, Ireland, Europe, NZ & Dubai since 2014. Head office in Nagpur, international office in San Jose (USA), growing presence in Pune & Dubai.
            </p>
            <div className="flex gap-3 mt-6">
              {[Instagram, Linkedin, Youtube].map((I, i) => (
                <a key={i} href="#" className="w-10 h-10 grid place-items-center rounded-full border-2 border-white hover:bg-[#c92e34] hover:border-[#c92e34] transition-colors" aria-label="social" data-testid={`footer-social-${i}`}>
                  <I size={16} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <div className="font-display text-xl mb-4">CONTACT</div>
            <ul className="space-y-3 text-sm text-white/75">
              <li className="flex items-start gap-2"><MapPin size={16} className="text-[#c92e34] mt-0.5" /> Plot 20, First Floor, Near Samata Layout Garden, North Ambazari Road, Nagpur</li>
              <li className="flex items-center gap-2">
                <Phone size={16} className="text-[#c92e34]" />
                <a
                  href="tel:+918530635656"
                  onClick={() => trackLead({ method: "phone", location: "footer" })}
                  data-testid="footer-phone-link"
                  className="hover:text-[#c92e34]"
                >
                  +91 85306 35656
                </a>
              </li>
              <li className="flex items-center gap-2"><Mail size={16} className="text-[#c92e34]" /> hello@shahoverseasedu.com</li>
            </ul>
          </div>

          <div>
            <div className="font-display text-xl mb-4">QUICK LINKS</div>
            <ul className="space-y-2 text-sm text-white/75">
              <li><a href="#destinations" className="hover:text-[#c92e34]">Destinations</a></li>
              <li><a href="#services" className="hover:text-[#c92e34]">Services</a></li>
              <li><a href="#story" className="hover:text-[#c92e34]">Our Story</a></li>
              <li><a href="/careers" className="hover:text-[#c92e34]" data-testid="footer-careers-link">Careers · We're Hiring</a></li>
              <li><a href="#faq" className="hover:text-[#c92e34]">FAQ</a></li>
              <li><a href="#inquiry" className="hover:text-[#c92e34]">Free Counselling</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-white/50">
          <div>© {new Date().getFullYear()} Shah Overseas Education. All rights reserved.</div>
          <div>Counselling: 10:30 AM – 6:30 PM · Mon–Sat</div>
        </div>

        {/* SEO-friendly service-area & keyword strip */}
        <nav aria-label="Popular study abroad searches" className="mt-8 text-[11px] leading-relaxed text-white/40">
          <div className="font-display text-white/70 text-xs tracking-widest uppercase mb-2">Popular Searches</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            <a href="#destinations" className="hover:text-white">Study in USA from India</a><span>·</span>
            <a href="#destinations" className="hover:text-white">Study in UK for Indian students</a><span>·</span>
            <a href="#destinations" className="hover:text-white">Study in Canada consultants</a><span>·</span>
            <a href="#destinations" className="hover:text-white">Study in Australia from India</a><span>·</span>
            <a href="#destinations" className="hover:text-white">Study in Ireland from India</a><span>·</span>
            <a href="#destinations" className="hover:text-white">Study in New Zealand</a><span>·</span>
            <a href="#destinations" className="hover:text-white">Study in Germany</a><span>·</span>
            <a href="#destinations" className="hover:text-white">Study in Dubai</a><span>·</span>
            <a href="#test-prep" className="hover:text-white">IELTS coaching in Nagpur</a><span>·</span>
            <a href="#test-prep" className="hover:text-white">TOEFL classes Nagpur</a><span>·</span>
            <a href="#test-prep" className="hover:text-white">GRE coaching Nagpur</a><span>·</span>
            <a href="#test-prep" className="hover:text-white">GMAT coaching Nagpur</a><span>·</span>
            <a href="#test-prep" className="hover:text-white">PTE academy Nagpur</a><span>·</span>
            <a href="#test-prep" className="hover:text-white">Duolingo test prep</a><span>·</span>
            <a href="#services" className="hover:text-white">SOP & LOR writing</a><span>·</span>
            <a href="#services" className="hover:text-white">Student visa consultants Nagpur</a><span>·</span>
            <a href="#services" className="hover:text-white">MS in USA consultants</a><span>·</span>
            <a href="#services" className="hover:text-white">MBA abroad consultants</a><span>·</span>
            <a href="/study-abroad-consultants-nagpur" className="hover:text-white">Study abroad consultants in Nagpur</a><span>·</span>
            <a href="/study-in-germany" className="hover:text-white">Study in Germany consultants</a><span>·</span>
            <a href="#inquiry" className="hover:text-white">Best overseas education consultancy in India</a>
          </div>
          <div className="mt-4 text-white/35">
            <span className="font-semibold text-white/55">Serving students from across Maharashtra:</span> Nagpur · Pune · Mumbai · Nashik · Aurangabad · Solapur · Kolhapur · Thane · Navi Mumbai · Amravati · Akola · Wardha · Chandrapur · Yavatmal · Bhandara · Gondia · Latur · Jalgaon · Sangli · Satara · Ahmednagar · Beed · Parbhani · Nanded — plus Raipur · Bhopal · Indore · Hyderabad · Bangalore · across India.
          </div>
        </nav>
      </div>
    </footer>
  );
}
